import { ChevronDown } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Bar } from '@apps/web/components/dither-kit/bar';
import { BarChart } from '@apps/web/components/dither-kit/bar-chart';
import { type ChartConfig } from '@apps/web/components/dither-kit/chart-context';
import { Grid } from '@apps/web/components/dither-kit/grid';
import { Legend } from '@apps/web/components/dither-kit/legend';
import { Tooltip } from '@apps/web/components/dither-kit/tooltip';
import { XAxis } from '@apps/web/components/dither-kit/x-axis';
import { YAxis } from '@apps/web/components/dither-kit/y-axis';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@apps/web/components/ui/collapsible';
import { Spinner } from '@apps/web/components/ui/spinner';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@apps/web/components/ui/table';
import { cn } from '@apps/web/lib/utils';
import { useAdminVoiceCallLatency } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type VoiceCallLatencySummary,
    type VoiceCallLatencyTurn,
} from '@libraries/typescript/api_client/schema_types';

// Stub height for a turn whose agent audio never matched, so an unanswered
// turn is visible on the chart instead of silently missing.
const UNMATCHED_MARKER_FRACTION = 0.04;

const COMMITTED_KEY = 'committed';
const ENDPOINTING_KEY = 'endpointing';
const UNMATCHED_KEY = 'unmatched';

/**
 * Per-turn "caller stopped speaking → Phoebe started speaking" latency for one
 * call. Collapsed by default: it is diagnostic detail, not the headline.
 */
export function LatencyPerTurnCard({ callId }: { readonly callId: string }) {
    const [open, setOpen] = useState(false);
    const { data, isLoading, error } = useAdminVoiceCallLatency(
        open ? callId : null,
    );

    return (
        <Collapsible open={open} onOpenChange={setOpen}>
            <CollapsibleTrigger asChild={true}>
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground -ml-2 gap-1"
                >
                    <ChevronDown
                        className={cn(
                            'h-4 w-4 transition-transform',
                            open ? 'rotate-180' : '',
                        )}
                    />
                    Response latency
                </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-3">
                {isLoading ? (
                    <div className="flex items-center gap-2">
                        <Spinner className="size-4" />
                        <span className="text-muted-foreground text-sm">
                            Loading latency…
                        </span>
                    </div>
                ) : error || !data ? (
                    <p className="text-muted-foreground text-sm">
                        Could not load latency for this call.
                    </p>
                ) : (
                    <LatencyDetail
                        summary={data.summary}
                        summarySource={data.summary_source}
                        turns={data.turns}
                    />
                )}
            </CollapsibleContent>
        </Collapsible>
    );
}

function LatencyDetail({
    summary,
    summarySource,
    turns,
}: {
    readonly summary: VoiceCallLatencySummary | null;
    readonly summarySource: 'stamped' | 'computed' | null;
    readonly turns: ReadonlyArray<VoiceCallLatencyTurn>;
}) {
    // Acoustic latency measures from the moment the caller stopped making
    // sound, so it always contains the committed latency plus the transcriber's
    // endpointing wait. Stacking that wait on top of the committed bar reads
    // the split directly, and keeps one full-width dithered bar per turn —
    // grouped bars at 30 turns are slivers too thin to carry the texture.
    const { chartData, config } = useMemo(() => {
        const totals = turns.map((turn) =>
            Math.max(turn.acoustic_latency_ms ?? 0, turn.latency_ms ?? 0),
        );
        const markerMs = Math.max(
            Math.round(Math.max(0, ...totals) * UNMATCHED_MARKER_FRACTION),
            1,
        );
        let hasEndpointing = false;
        let hasUnmatched = false;
        const rows = turns.map((turn) => {
            const committed = turn.latency_ms;
            const acoustic = turn.acoustic_latency_ms ?? null;
            const endpointing =
                committed === null || acoustic === null
                    ? 0
                    : Math.max(0, acoustic - committed);
            hasEndpointing = hasEndpointing || endpointing > 0;
            hasUnmatched = hasUnmatched || committed === null;
            return {
                turn: turn.turn_index,
                turnLabel: `Turn ${turn.turn_index}`,
                [COMMITTED_KEY]: committed ?? 0,
                [ENDPOINTING_KEY]: endpointing,
                [UNMATCHED_KEY]: committed === null ? markerMs : 0,
            };
        });
        const built: ChartConfig = {
            [COMMITTED_KEY]: { label: 'Committed', color: 'blue' },
        };
        if (hasEndpointing) {
            built[ENDPOINTING_KEY] = {
                label: 'Endpointing wait',
                color: 'purple',
            };
        }
        if (hasUnmatched) {
            built[UNMATCHED_KEY] = { label: 'No agent audio', color: 'grey' };
        }
        return { chartData: rows, config: built };
    }, [turns]);

    if (summary === null && turns.length === 0) {
        return (
            <p className="text-muted-foreground text-sm">
                No latency samples for this call. Samples come from the
                developer transcript, which only recent calls carry.
            </p>
        );
    }

    const acousticCount = summary?.acoustic_count ?? 0;

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
                <span className="text-muted-foreground text-xs">Committed</span>
                <SummaryChip label="avg" value={summary?.avg_ms ?? null} />
                <SummaryChip label="p50" value={summary?.p50_ms ?? null} />
                <SummaryChip label="p95" value={summary?.p95_ms ?? null} />
                <Badge variant="outline">{summary?.count ?? 0} samples</Badge>
                {summary && summary.unmatched_turn_count > 0 ? (
                    <Badge
                        variant="outline"
                        className="border-amber-200 text-amber-800"
                    >
                        {summary.unmatched_turn_count} unmatched
                    </Badge>
                ) : null}
                {summarySource ? (
                    <span className="text-muted-foreground text-xs">
                        {summarySource === 'stamped'
                            ? 'from call metadata'
                            : 'recomputed from the transcript'}
                    </span>
                ) : null}
            </div>
            {acousticCount > 0 ? (
                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-muted-foreground text-xs">
                        Acoustic
                    </span>
                    <SummaryChip
                        label="avg"
                        value={summary?.acoustic_avg_ms ?? null}
                    />
                    <SummaryChip
                        label="p50"
                        value={summary?.acoustic_p50_ms ?? null}
                    />
                    <SummaryChip
                        label="p95"
                        value={summary?.acoustic_p95_ms ?? null}
                    />
                    <Badge variant="outline">{acousticCount} samples</Badge>
                    <span className="text-muted-foreground text-xs">
                        includes the speech-to-text endpointing wait
                    </span>
                </div>
            ) : null}
            {chartData.length > 0 ? (
                <div className="bg-card h-[190px] w-full rounded-md border p-2">
                    <BarChart
                        data={chartData}
                        config={config}
                        stackType="stacked"
                        margins={{ top: 26, right: 12, bottom: 22, left: 44 }}
                        bloom="off"
                    >
                        <Grid horizontal={true} />
                        <XAxis dataKey="turn" />
                        <YAxis tickFormatter={(value) => String(value)} />
                        {Object.keys(config).map((key) => (
                            <Bar
                                key={key}
                                dataKey={key}
                                variant="gradient"
                                isClickable={true}
                            />
                        ))}
                        <Legend isClickable={true} align="right" />
                        <Tooltip
                            labelKey="turnLabel"
                            // The marker series is a fixed stub height, and a
                            // stacked layer that is zero on this turn has
                            // nothing to report — neither is a millisecond
                            // reading, so neither prints one.
                            valueFormatter={(value, name) =>
                                value === 0
                                    ? '—'
                                    : name === UNMATCHED_KEY
                                      ? 'no agent audio'
                                      : `${Math.round(value)} ms`
                            }
                        />
                    </BarChart>
                </div>
            ) : null}
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead className="w-[60px]">Turn</TableHead>
                        <TableHead>Caller stopped</TableHead>
                        <TableHead>Agent audio</TableHead>
                        <TableHead className="text-right">Latency</TableHead>
                        <TableHead className="text-right">Acoustic</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {turns.map((turn) => (
                        <TableRow key={turn.turn_index}>
                            <TableCell className="tabular-nums">
                                {turn.turn_index}
                            </TableCell>
                            <TableCell className="text-muted-foreground text-xs">
                                {formatTimestamp(turn.user_turn_stopped_at)}
                            </TableCell>
                            <TableCell className="text-muted-foreground text-xs">
                                {formatTimestamp(turn.tts_audio_started_at)}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                                {turn.latency_ms === null ? (
                                    <span className="text-muted-foreground">
                                        unmatched
                                    </span>
                                ) : (
                                    `${turn.latency_ms} ms`
                                )}
                            </TableCell>
                            <TableCell className="text-muted-foreground text-right tabular-nums">
                                {typeof turn.acoustic_latency_ms === 'number'
                                    ? `${turn.acoustic_latency_ms} ms`
                                    : '—'}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
}

function SummaryChip({
    label,
    value,
}: {
    readonly label: string;
    readonly value: number | null;
}) {
    return (
        <Badge variant="secondary" className="gap-1 tabular-nums">
            <span className="text-muted-foreground">{label}</span>
            {value === null ? '—' : `${Math.round(value)} ms`}
        </Badge>
    );
}

function formatTimestamp(value: string | null): string {
    if (!value) {
        return '—';
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return value;
    }
    return parsed.toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
}
